import React, { useMemo } from 'react';
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Drawer,
  IconButton,
  Paper,
  Typography,
  alpha,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import HistoryIcon from '@mui/icons-material/History';
import TimelineIcon from '@mui/icons-material/Timeline';
import moment from 'moment';
import {
  getBookingAuditActorChipSx,
  getBookingAuditDrawerHeaderSx,
  getBookingAuditEmptyStateSx,
  getBookingAuditEventCardSx,
  getBookingAuditEventTypeChipSx,
  getBookingAuditRefreshButtonSx,
  getBookingAuditRefreshRowSx,
  getBookingAuditTimelineAvatarSx,
  getBookingAuditTimelineConnectorSx,
} from '../../../utils/bookingAuditLayout';
import { HALL_FORM_ACCENT, getHallDrawerPaperSx } from '../utils/hallDrawerStyles';

function formatActivityTime(value) {
  if (!value) return '—';
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format('DD MMM YYYY, hh:mm A') : String(value);
}

function getActivityPresentation(eventType) {
  const key = String(eventType || '').toLowerCase();
  if (key.includes('vendor_payment') || key.includes('payment')) {
    return { color: HALL_FORM_ACCENT, icon: <FactCheckIcon sx={{ fontSize: 16 }} /> };
  }
  if (key.includes('vendor')) {
    return { color: '#38bdf8', icon: <TimelineIcon sx={{ fontSize: 16 }} /> };
  }
  if (key.includes('cancel')) {
    return { color: '#f07178', icon: <HistoryIcon sx={{ fontSize: 16 }} /> };
  }
  return { color: '#5b9cf5', icon: <TimelineIcon sx={{ fontSize: 16 }} /> };
}

function formatMoneyLabel(value) {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return '—';
  return `₹${amount.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
}

function formatChangeValue(key, value) {
  if (value == null || value === '') return '—';
  if (key === 'contract_amount' || key === 'paid_amount' || key === 'due_amount' || key === 'amount') {
    return formatMoneyLabel(value);
  }
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
}

function VendorAuditDetails({ payload, isDark }) {
  if (!payload || typeof payload !== 'object') return null;
  const vendor = payload.vendor || {};
  const assignment = payload.assignment || {};
  const payment = payload.payment || {};
  const changes = payload.changes && typeof payload.changes === 'object' ? payload.changes : null;
  const hasVendorScope = String(payload?.metadata?.scope || '') === 'booking_vendor_assignment'
    || Boolean(vendor?.name || assignment?.id || payment?.amount != null);
  if (!hasVendorScope) return null;

  const rows = [
    vendor?.name ? { label: 'Vendor', value: vendor.name } : null,
    assignment?.task ? { label: 'Task', value: assignment.task } : null,
    assignment?.contract_amount != null
      ? { label: 'Contract', value: formatMoneyLabel(assignment.contract_amount) }
      : null,
    assignment?.paid_amount != null
      ? { label: 'Paid', value: formatMoneyLabel(assignment.paid_amount) }
      : null,
    assignment?.due_amount != null
      ? { label: 'Due', value: formatMoneyLabel(assignment.due_amount) }
      : null,
    assignment?.include_in_hall != null
      ? {
        label: 'Hall collection',
        value: assignment.include_in_hall ? 'Included' : 'Excluded',
      }
      : null,
    payment?.amount != null
      ? {
        label: 'Payout',
        value: [
          formatMoneyLabel(payment.amount),
          payment.payment_type_label || payment.payment_type,
          payment.mode || payment.payment_method,
        ].filter(Boolean).join(' · '),
      }
      : null,
  ].filter(Boolean);

  const changeRows = changes
    ? Object.entries(changes).map(([key, value]) => ({
      label: String(key).replace(/_/g, ' '),
      value: `${formatChangeValue(key, value?.from)} → ${formatChangeValue(key, value?.to)}`,
    }))
    : [];

  if (!rows.length && !changeRows.length) return null;

  return (
    <Box
      sx={{
        mt: 1,
        p: 1,
        borderRadius: 1.5,
        border: `1px solid ${isDark ? alpha('#fff', 0.1) : alpha('#0f172a', 0.08)}`,
        backgroundColor: isDark ? alpha('#0f172a', 0.45) : alpha('#f8fafc', 0.9),
      }}
    >
      {[...rows, ...changeRows].map((row) => (
        <Box
          key={`${row.label}-${row.value}`}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 1.25,
            py: 0.25,
          }}
        >
          <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b', fontWeight: 700 }}>
            {row.label}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: isDark ? '#e2e8f0' : '#334155',
              fontWeight: 700,
              textAlign: 'right',
              whiteSpace: 'pre-line',
            }}
          >
            {row.value}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

/**
 * Right-side hall audit / activity drawer (same pattern as BookingAuditDrawer).
 */
export default function HallAuditDrawer({
  open,
  onClose,
  hallName,
  hallCode,
  title = 'Hall Audit',
  emptyMessage = 'No hall activity found yet.',
  events = [],
  isDark = false,
  loading = false,
  onRefresh,
}) {
  const activity = useMemo(
    () => (Array.isArray(events) ? events : []),
    [events],
  );

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      ModalProps={{
        // Sit above nested hall booking drawers when opened from Event Overview.
        sx: { zIndex: (theme) => theme.zIndex.modal + 2 },
      }}
      PaperProps={{
        sx: {
          ...getHallDrawerPaperSx(isDark),
          background: isDark
            ? `linear-gradient(165deg, #07111f 0%, #0b1a2a 55%, #0a1f1a 100%)`
            : `linear-gradient(165deg, #ffffff 0%, #f7fbff 55%, #f3fbf7 100%)`,
        },
      }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={getBookingAuditDrawerHeaderSx(isDark)}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            <FactCheckIcon sx={{ color: HALL_FORM_ACCENT, fontSize: 24, flexShrink: 0 }} />
            <Typography
              variant="h6"
              sx={{ color: isDark ? '#f8fafc' : '#0f172a', fontWeight: 700 }}
            >
              {title}
            </Typography>
            {(hallCode || hallName) && (
              <Chip
                size="small"
                label={hallCode || hallName}
                sx={{
                  fontWeight: 800,
                  color: isDark ? '#e2e8f0' : '#334155',
                  bgcolor: isDark ? alpha('#fff', 0.08) : alpha('#0f172a', 0.06),
                  border: `1px solid ${isDark ? alpha('#fff', 0.12) : alpha('#0f172a', 0.08)}`,
                }}
              />
            )}
          </Box>
          <IconButton onClick={onClose} sx={{ color: isDark ? '#94a3b8' : '#64748b' }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {typeof onRefresh === 'function' ? (
          <Box sx={getBookingAuditRefreshRowSx(isDark)}>
            <Button
              variant="text"
              onClick={onRefresh}
              disabled={loading}
              sx={getBookingAuditRefreshButtonSx(isDark)}
            >
              {loading ? 'Refreshing timeline...' : 'Refresh timeline'}
            </Button>
          </Box>
        ) : null}

        <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
          {loading ? (
            <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={28} sx={{ color: HALL_FORM_ACCENT }} />
            </Box>
          ) : null}

          {!loading && activity.length === 0 ? (
            <Paper elevation={0} sx={getBookingAuditEmptyStateSx(isDark)}>
              <Typography variant="body2" sx={{ color: isDark ? '#94a3b8' : '#64748b' }}>
                {emptyMessage}
              </Typography>
            </Paper>
          ) : null}

          {!loading &&
            activity.map((event, index) => {
              const presentation = getActivityPresentation(event?.type || event?.event_type);
              const isLast = index === activity.length - 1;
              const actorLabel = event?.actor || 'System';

              return (
                <Box
                  key={event.id || `${event.type || event.title}-${index}`}
                  sx={{ display: 'flex', gap: 1.5, pb: isLast ? 0 : 2 }}
                >
                  <Box sx={{ width: 28, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Avatar sx={getBookingAuditTimelineAvatarSx(isDark, presentation.color)}>
                      {presentation.icon}
                    </Avatar>
                    {!isLast ? <Box sx={getBookingAuditTimelineConnectorSx(isDark)} /> : null}
                  </Box>

                  <Paper elevation={0} sx={getBookingAuditEventCardSx(isDark)}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                      <Typography
                        variant="subtitle2"
                        sx={{ color: isDark ? '#f8fafc' : '#0f172a', fontWeight: 700 }}
                      >
                        {event?.title || 'Hall activity'}
                      </Typography>
                      <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b' }}>
                        {formatActivityTime(event?.created_at)}
                      </Typography>
                    </Box>
                    {event?.description ? (
                      <Typography
                        variant="body2"
                        sx={{ color: isDark ? '#cbd5e1' : '#475569', mt: 0.5 }}
                      >
                        {event.description}
                      </Typography>
                    ) : null}
                    <VendorAuditDetails payload={event?.payload} isDark={isDark} />
                    <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Chip size="small" label={`By ${actorLabel}`} sx={getBookingAuditActorChipSx(isDark)} />
                      {event?.type ? (
                        <Chip
                          size="small"
                          label={String(event.type).replace(/_/g, ' ')}
                          sx={getBookingAuditEventTypeChipSx(isDark, presentation.color)}
                        />
                      ) : null}
                    </Box>
                  </Paper>
                </Box>
              );
            })}
        </Box>
      </Box>
    </Drawer>
  );
}
